<script lang="ts" setup>
import type { ComponentDynamicTransportCountry, GetTransportsQuery } from '@autobid/strapi-integration/graphql/graphql'
import type { DynamicTransportCountry } from '@autobid/strapi-integration/rest/components'
import { getError } from '@autobid/strapi-integration/utils/getError'
import { removeReference } from '@autobid/strapi-integration/utils/helpers'
import { useQuery } from '@tanstack/vue-query'
import { onServerPrefetch } from 'vue'
import TransportAccordion from './partials/TransportAccordion.vue'
import TransportTable from './partials/TransportTable.vue'

interface Props {
  data: ComponentDynamicTransportCountry | DynamicTransportCountry
}

const props = defineProps<Props>()
const { t } = useI18n()
const { $trpc } = useNuxtApp()

const componentName = {
  accordion: TransportAccordion,
  table: TransportTable,
}[props.data.type]

function sortCountriesAndCompanies(data: GetTransportsQuery['transportCountries']): GetTransportsQuery['transportCountries'] {
  let result = removeReference(data)

  const getCountryLabel = (name: string) =>
    t(`country.${name.toLocaleLowerCase()}`)

  // filter out countries without companies
  result = result
    .filter(country => country && country.companies.length)
    .map((country) => {
      if (!country) {
        return country
      }

      return {
        ...country,
        name: getCountryLabel(country.name),
      }
    })

  // sort countries by a rank
  result.sort((a, b) => {
    if (!a || !b) {
      return 0
    }

    // exception: EU area MUST be at the beggining of the list
    if (a.name === getCountryLabel('gesamter eu-raum'))
      return -1

    return a.name.toLocaleLowerCase()
      > b.name.toLocaleLowerCase()
      ? 1
      : -1
  })

  // sort companies ascending by a name
  for (const country of result) {
    if (!country) {
      continue
    }

    country.companies.sort((a, b) => {
      if (!a || !b) {
        return 0
      }

      return a.name.toLocaleLowerCase()
        > b.name.toLocaleLowerCase()
        ? 1
        : -1
    },
    )

    country.companies.forEach((company) => {
      if (!company?.emails) {
        return
      }

      const emailsThatMatchCurrentCountry = company.emails.filter(
        (companyEmail) => {
          if (!companyEmail) {
            return false
          }

          const countriesNames = companyEmail.countries.filter(el => !!el).map(el =>
            getCountryLabel(el.name),
          )

          return countriesNames.includes(country.name)
        },
      )

      company.emails = emailsThatMatchCurrentCountry
    })
  }

  return result
}

const { suspense, data: transports } = useQuery(
  ['transports'],
  async () => {
    try {
      const result = await $trpc.cms.getTransports.query()

      if (!result)
        return null

      // todo: try to move this parsing to the api side
      return sortCountriesAndCompanies(result)
    }
    catch (error) {
      const { message, code, handled } = getError(error)

      if (!handled) {
        showError({ statusCode: code, statusMessage: message })
      }
    }
  },
  {
    refetchOnWindowFocus: false,
  },
)
onServerPrefetch(suspense)
</script>

<template>
  <div v-if="transports">
    <component :is="componentName" :transports="transports" />
  </div>
</template>
